// Minimal browser client for the same-origin Share-A-Cart MCP server (/mcp).
// The server runs in stateless Streamable-HTTP mode, so each JSON-RPC request is
// an independent POST — no initialize handshake or session id required. Responses
// come back as a single Server-Sent-Events frame (text/event-stream).

const MCP_ENDPOINT = '/mcp';

export interface McpContentBlock {
  type: string;
  text?: string;
  [key: string]: unknown;
}

export interface McpCallResult {
  content?: McpContentBlock[];
  structuredContent?: unknown;
  isError?: boolean;
}

let rpcId = 0;

function parseSse(text: string): unknown[] {
  const events: unknown[] = [];
  for (const block of text.split(/\r?\n\r?\n/)) {
    const data = block
      .split(/\r?\n/)
      .filter((l) => l.startsWith('data:'))
      .map((l) => l.slice(5).trimStart())
      .join('\n');
    if (!data) continue;
    try {
      events.push(JSON.parse(data));
    } catch {
      /* ignore keep-alive / non-JSON frames */
    }
  }
  return events;
}

async function rpc<T>(method: string, params: Record<string, unknown>): Promise<T> {
  const id = ++rpcId;
  const res = await fetch(MCP_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json, text/event-stream',
    },
    body: JSON.stringify({ jsonrpc: '2.0', id, method, params }),
  });

  if (!res.ok) {
    throw new Error(`MCP ${method} failed: HTTP ${res.status}`);
  }

  const contentType = res.headers.get('content-type') || '';
  const raw = await res.text();

  const messages = contentType.includes('text/event-stream')
    ? parseSse(raw)
    : (() => {
        try {
          return [JSON.parse(raw)];
        } catch {
          return [];
        }
      })();

  const reply = messages.find(
    (m): m is { id: number; result?: T; error?: { message: string } } =>
      !!m && typeof m === 'object' && (m as { id?: number }).id === id,
  );

  if (!reply) throw new Error(`MCP ${method}: no matching response`);
  if (reply.error) throw new Error(`MCP ${method}: ${reply.error.message}`);
  return reply.result as T;
}

export async function mcpCallTool(
  name: string,
  args: Record<string, unknown>,
): Promise<string> {
  const result = await rpc<McpCallResult>('tools/call', {
    name,
    arguments: args || {},
  });

  const text = (result.content || [])
    .filter((c) => c.type === 'text' && typeof c.text === 'string')
    .map((c) => c.text)
    .join('\n')
    .trim();

  if (text) return text;
  if (result.structuredContent !== undefined) {
    return JSON.stringify(result.structuredContent);
  }
  return result.isError ? 'Tool returned an error.' : 'Done.';
}
